import { Component, OnDestroy } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { MatDialogRef } from "@angular/material/dialog";
import { faSave, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { NotifierService } from "angular-notifier";
import { merge, Subject, takeUntil } from "rxjs";
import { STORAGE_KEYS } from "../../constants";
import { SecurityService } from "../../services/security/security.service";
import { UserInfoService } from "../../services/user-info.service";

type StrengthState = "danger" | "warning" | "success" | "info";
type ChangePasswordForm = FormGroup<{
  oldPassword: FormControl<string | null>;
  newPassword: FormControl<string | null>;
  confirmPassword: FormControl<string | null>;
}>;

@Component({
  selector: "app-change-password",
  templateUrl: "./change-password.component.html",
  styleUrls: ["./change-password.component.css"],
})
export class ChangePasswordComponent implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly userId: string;

  form: ChangePasswordForm;

  strengthMessage = "";
  strengthState: StrengthState = "danger";
  passwordValid = false;
  checkingStrength = false;
  saving = false;
  faSave = faSave;
  faTimesCircle = faTimesCircle;

  constructor(
    private formBuilder: FormBuilder,
    private dialogRef: MatDialogRef<ChangePasswordComponent>,
    private notifier: NotifierService,
    private securityService: SecurityService,
    userInfoService: UserInfoService,
  ) {
    this.form = this.formBuilder.group({
      oldPassword: ["", [Validators.required, Validators.maxLength(100)]],
      newPassword: ["", [Validators.required, Validators.maxLength(100)]],
      confirmPassword: ["", [Validators.required, Validators.maxLength(100)]],
    });

    const userInfo = userInfoService.getUserInfo();
    this.userId = userInfo[STORAGE_KEYS.USER_INFO["USER_ID"].sessionKey] ?? "";

    merge(
      this.form.controls.newPassword.valueChanges,
      this.form.controls.confirmPassword.valueChanges,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.passwordValid = false;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  checkPasswordStrength(): void {
    this.passwordValid = false;

    const newPassword = this.form.controls.newPassword.value ?? "";
    const confirmPassword = this.form.controls.confirmPassword.value ?? "";

    if (!newPassword && !confirmPassword) {
      this.strengthMessage = "";
      this.strengthState = "info";
      return;
    }

    if (newPassword !== confirmPassword) {
      this.strengthMessage = "Password and Confirm password did not match.";
      this.strengthState = "danger";
      return;
    }

    this.checkingStrength = true;
    this.securityService
      .checkStrength(confirmPassword)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const score = response.body;
          this.passwordValid = score > 3;
          this.strengthState =
            score < 3 ? "danger" : score === 3 ? "warning" : "success";
          this.strengthMessage =
            score < 3
              ? "Password strength is Weak"
              : score === 3
                ? "Password strength is Medium"
                : "Password strength is Strong";
          this.checkingStrength = false;
        },
        error: (error) => {
          this.checkingStrength = false;
          this.notifier.notify(
            "error",
            error?.error?.Message ?? error?.message ?? "Error checking password strength",
          );
        },
      });
  }

  save(): void {
    if (!this.passwordValid || this.form.invalid || this.saving) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;
    const value = this.form.getRawValue();

    this.securityService
      .updatePassword({
        userid: this.userId,
        oldPassword: value.oldPassword ?? "",
        newPassword: value.newPassword ?? "",
        confirmPassword: value.confirmPassword ?? "",
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const result = response.body;
          this.saving = false;

          if (result?.IsError) {
            this.notifier.notify("error", result.Message ?? "Error updating password");
            return;
          }

          this.notifier.notify("success", result?.Message ?? "Password updated successfully");
          this.dialogRef.close(true);
        },
        error: (error) => {
          this.saving = false;
          this.notifier.notify(
            "error",
            error?.error?.Message ?? error?.message ?? "Error updating password",
          );
        },
      });
  }

  close(): void {
    this.dialogRef.close(false);
  }
}
